import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "@/components/Button/Button";
import type {
  ComponentPageData,
  RawQuestionSettings,
} from "@/features/content/useComponent";
import {
  resolveAiHintThreshold,
  resolveAllowAiAssistant,
  resolveAllowAiHints,
  resolveAllowAiPracticeProblems,
} from "@/features/questions/questionSettingsMerge";

import { useUpdateComponentSettings } from "./hooks/useUpdateComponentSettings";
import styles from "./AISettingsDialog.module.css";

interface AISettingsDialogProps {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  // The course-level defaults these settings fall back to. Naming the concrete
  // value inside each "Use course default" option is the whole point: otherwise
  // the instructor is choosing an outcome they cannot see.
  courseQuestionSettings?: RawQuestionSettings | null;
  onOpenChange(nextOpen: boolean): void;
  open: boolean;
}

function describeAttempts(threshold: number) {
  if (threshold <= 0) {
    return "Right away";
  }

  return `After ${threshold} attempt${threshold === 1 ? "" : "s"}`;
}

// '' means "use the course default": the key is OMITTED on save so the
// course-level value keeps applying. An explicit '0'/'1' is written and
// overrides the course. Writing 0 for everything is what used to freeze a
// course's defaults onto a chapter forever.
type InheritableFlag = "" | "0" | "1";

interface AIFormState {
  allowAiAssistant: InheritableFlag;
  allowAiHints: InheritableFlag;
  allowAiPracticeProblems: InheritableFlag;
  // '' = inherit; otherwise the number of attempts before an AI hint appears.
  aiHintAttempts: string;
}

function readFlag(
  settings: RawQuestionSettings | null | undefined,
  snakeKey: string,
  camelKey: string,
): InheritableFlag {
  if (!settings) {
    return "";
  }
  const raw =
    (settings as Record<string, unknown>)[snakeKey] ??
    (settings as Record<string, unknown>)[camelKey];
  if (raw === null || raw === undefined || raw === "") {
    return "";
  }
  return Number(raw) === 1 ? "1" : "0";
}

function readNumber(
  settings: RawQuestionSettings | null | undefined,
  snakeKey: string,
  camelKey: string,
): string {
  if (!settings) {
    return "";
  }
  const raw =
    (settings as Record<string, unknown>)[snakeKey] ??
    (settings as Record<string, unknown>)[camelKey];
  if (raw === null || raw === undefined || raw === "") {
    return "";
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? String(Math.max(0, parsed)) : "";
}

// Reads the component's OWN settings only — never the course's. The dialog edits
// the component override, so an inherited value must show as "Use course
// default" rather than as an explicit choice the instructor never made.
function getComponentSettings(
  component: ComponentPageData,
): RawQuestionSettings | null {
  const raw =
    component.rawComponent.question_settings ??
    component.rawComponent.questionSettings;
  return (raw as RawQuestionSettings | undefined) ?? null;
}

function getInitialState(component: ComponentPageData): AIFormState {
  const settings = getComponentSettings(component);
  return {
    aiHintAttempts: readNumber(
      settings,
      "num_attempts_to_show_ai_hints",
      "numAttemptsToShowAiHints",
    ),
    allowAiAssistant: readFlag(
      settings,
      "allow_ai_assistant",
      "allowAiAssistant",
    ),
    allowAiHints: readFlag(settings, "allow_ai_hints", "allowAiHints"),
    allowAiPracticeProblems: readFlag(
      settings,
      "allow_ai_practice_problems",
      "allowAiPracticeProblems",
    ),
  };
}

export function AISettingsDialog({
  component,
  componentId,
  courseId,
  courseQuestionSettings,
  onOpenChange,
  open,
}: AISettingsDialogProps) {
  const mutation = useUpdateComponentSettings();
  // Resolved exactly the way a question resolves them, so the label matches what
  // students actually get when this chapter inherits.
  const courseDefaults = {
    aiHintAttempts: describeAttempts(
      resolveAiHintThreshold(courseQuestionSettings),
    ),
    allowAiAssistant: resolveAllowAiAssistant(courseQuestionSettings)
      ? "On"
      : "Off",
    allowAiHints: resolveAllowAiHints(courseQuestionSettings) ? "On" : "Off",
    allowAiPracticeProblems: resolveAllowAiPracticeProblems(
      courseQuestionSettings,
    )
      ? "On"
      : "Off",
  };
  const [formState, setFormState] = useState<AIFormState>(() =>
    getInitialState(component),
  );

  useEffect(() => {
    if (open) {
      setFormState(getInitialState(component));
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSave() {
    await mutation.mutateAsync({
      applyChanges: (draft) => {
        const current = (draft.question_settings ??
          draft.questionSettings ??
          {}) as RawQuestionSettings;
        const next: Record<string, unknown> = { ...current };

        // Inheriting DELETES the key. The merge that resolves settings skips
        // only null/undefined, so leaving an explicit 0 behind would silently
        // override any later course-level change.
        const applyFlag = (
          key: string,
          camelKey: string,
          value: InheritableFlag,
        ) => {
          delete next[camelKey];
          if (value === "") {
            delete next[key];
            return;
          }
          next[key] = Number(value);
        };

        applyFlag("allow_ai_hints", "allowAiHints", formState.allowAiHints);
        applyFlag(
          "allow_ai_practice_problems",
          "allowAiPracticeProblems",
          formState.allowAiPracticeProblems,
        );
        applyFlag(
          "allow_ai_assistant",
          "allowAiAssistant",
          formState.allowAiAssistant,
        );

        delete next.numAttemptsToShowAiHints;
        if (formState.aiHintAttempts === "") {
          delete next.num_attempts_to_show_ai_hints;
        } else {
          next.num_attempts_to_show_ai_hints = Number(formState.aiHintAttempts);
        }

        draft.question_settings = next as RawQuestionSettings;
        draft.questionSettings = next as RawQuestionSettings;
      },
      component,
      componentId,
      courseId,
    });
    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>AI features</Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.subtitle}>
                Each setting can follow the course default or be set just for
                this chapter. These are independent of the authored hint
                settings.
              </p>
            </Dialog.Description>
          </div>

          <div className={styles.body}>
            <label className={styles.selectField}>
              <span className={styles.fieldLabel}>AI generated hints</span>
              <select
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    allowAiHints: event.target.value as InheritableFlag,
                  }))
                }
                value={formState.allowAiHints}
              >
                <option value="">
                  Use course default ({courseDefaults.allowAiHints})
                </option>
                <option value="1">On</option>
                <option value="0">Off</option>
              </select>
            </label>

            <label className={styles.selectField}>
              <span className={styles.fieldLabel}>
                Show AI hints after attempts
              </span>
              <select
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    aiHintAttempts: event.target.value,
                  }))
                }
                value={formState.aiHintAttempts}
              >
                <option value="">
                  Use course default ({courseDefaults.aiHintAttempts})
                </option>
                <option value="0">Right away</option>
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={String(value)}>
                    After {value} attempt{value === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.selectField}>
              <span className={styles.fieldLabel}>AI practice problems</span>
              <select
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    allowAiPracticeProblems: event.target
                      .value as InheritableFlag,
                  }))
                }
                value={formState.allowAiPracticeProblems}
              >
                <option value="">
                  Use course default ({courseDefaults.allowAiPracticeProblems})
                </option>
                <option value="1">On</option>
                <option value="0">Off</option>
              </select>
            </label>

            <label className={styles.selectField}>
              <span className={styles.fieldLabel}>
                Kai AI assistant in this chapter
              </span>
              <select
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    allowAiAssistant: event.target.value as InheritableFlag,
                  }))
                }
                value={formState.allowAiAssistant}
              >
                <option value="">
                  Use course default ({courseDefaults.allowAiAssistant})
                </option>
                <option value="1">On</option>
                <option value="0">Off</option>
              </select>
            </label>
          </div>

          {mutation.isError ? (
            <p className={styles.error} role="alert">
              Could not save the AI settings. Please try again.
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Dialog.Close>
            <Button disabled={mutation.isPending} onClick={handleSave}>
              {mutation.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
